import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import type { TableDataSource, TablePage } from '../data/table-data-source';
import type {
  RowActionsConfig,
  TableColumnConfig,
  TableSettingsConfig,
} from '../data/table-column-config';
import { MatTableModule } from '@angular/material/table';
import { TranslocoDirective } from '@jsverse/transloco';
import { TablePaginatorComponent } from '../table-paginator/table-paginator.component';
import { SettingsCellComponent } from '../cells/settings/settings-cell.component';
import { MatIcon } from '@angular/material/icon';
import { catchError, map, Observable, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { TableStateSelectors } from '../state/table-state.selectors';
import { TableSortConfigException } from '../exception/table-sort-config-exception';
import { MatSortModule } from '@angular/material/sort';
import { TableSortDirective } from '../table-sort/table-sort.directive';
import { I18nProviderInjectionToken } from '../i18n/i18n.scope';
import { isNil } from '@pct-ts/typeguard';
import { TableInvalidConfigException } from '../exception/table-invalid-config-exception';
import { ResolveTranslateAbleTextPipe } from '@pct-ng/i18n';
import { provideI18nScope } from '@pct-ng/transloco';
import { ObservableTableDataSource } from '../data/observable-table-data-source';
import { StaticArrayDataSource } from '../data/static-array-data-source';
import {
  CallPipe,
  IsConstructorPipe,
  IsNilPipe,
  IsStringResolverPipe,
  ResolveStringPipe,
} from '@pct-ng/common/pipe';
import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import { CustomMatSvgIconDirective } from '@pct-ng/icon';
import { areThereDuplicationInArray } from '@pct-ts/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ResolveCellContentInputsPipe } from '../pipe/resolve-cell-content-inputs.pipe';
import { ChangeColumnVisibilityAction } from '../state/action/change-column-visibility.action';
import { handleHttpError } from '@pct-ng/core/error-http';

@Component({
  selector: 'pct-feature-table-material-table',
  imports: [
    MatTableModule,
    TranslocoDirective,
    TablePaginatorComponent,
    IsConstructorPipe,
    SettingsCellComponent,
    MatIcon,
    MatSortModule,
    TableSortDirective,
    ResolveTranslateAbleTextPipe,
    IsStringResolverPipe,
    ResolveStringPipe,
    NgComponentOutlet,
    CallPipe,
    IsNilPipe,
    AsyncPipe,
    CustomMatSvgIconDirective,
    MatProgressSpinner,
    ResolveCellContentInputsPipe,
  ],
  templateUrl: './table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideI18nScope(I18nProviderInjectionToken)],
  host: {
    class: 'block relative w-full h-full',
  },
})
export class TableComponent<T> {
  protected readonly i18nScope = inject<string>(I18nProviderInjectionToken);
  readonly #store = inject(Store);

  readonly paginationSizeOptions = input([10, 20, 50, 100]);
  readonly id = input.required<string>();
  readonly source = input.required<T[] | Observable<T[]> | TableDataSource<T>>();
  readonly columnsConfig = input.required<TableColumnConfig<T>[]>();
  readonly rowActionsConfig = input<RowActionsConfig<T>>();
  readonly tableSettings = input<TableSettingsConfig>();
  readonly showPaginator = input<boolean>(true);

  protected storedDisplayedColumns = computed(() =>
    this.#store.selectSignal(TableStateSelectors.getDisplayedColumns(this.id()))(),
  );
  protected displayedColumns = computed(() => this.#getDisplayedColumns());
  protected displayedColumnIds = computed(() => this.#getDisplayedColumIds());

  protected readonly dataSource = computed(() => this.#createDataSource());
  protected dataInstance: Observable<TablePage<T>> = of();

  constructor() {
    effect(() => {
      this.#verifyConfigIsValid();
      untracked(() => {
        // TODO refactor @Mate
        const defaultHiddenColumns = this.tableSettings()?.defaultHiddenColumns;
        if (Array.isArray(defaultHiddenColumns)) {
          const displayedColumnIds = this.displayedColumnIds();
          // quick fix: load state and merge
          const displayedColumns = this.#store.selectSnapshot(
            TableStateSelectors.getDisplayedColumns(this.id()),
          );

          this.#store.dispatch(
            new ChangeColumnVisibilityAction({
              tableId: this.id(),
              displayedColumns:
                displayedColumns ??
                displayedColumnIds.filter(columnId => !defaultHiddenColumns.includes(columnId)),
            }),
          );
        }
      });
    });
    effect(() => this.#setupDataLoading());
  }

  #setupDataLoading(): void {
    this.dataInstance = this.dataSource()
      .getData$(this.id())
      .pipe(
        catchError(error =>
          new Observable<TablePage<T>>(observer =>
            handleHttpError(() => {
              observer.next(error);
              observer.complete();
            })(error),
          ).pipe(
            // TODO check is a FieldError, and when is it, send errors to filters
            // TODO @Mate load previous success response
            map(() => ({
              data: [],
              pagination: { page: 1, size: 10, totalCount: 0 },
            })),
          ),
        ),
      );
  }

  #createDataSource(): TableDataSource<T> {
    const rawDataSource = this.source();
    if (Array.isArray(rawDataSource)) {
      this.#verifyNoSortAndNoPagination();
      return new StaticArrayDataSource(rawDataSource);
    }
    if (rawDataSource instanceof Observable) {
      this.#verifyNoSortAndNoPagination();
      return new ObservableTableDataSource(rawDataSource);
    }
    return rawDataSource;
  }

  #getDisplayedColumns(): TableColumnConfig<T>[] {
    const columnIdsToShow = this.storedDisplayedColumns();

    // note: if it is not defined it means all column should be displayed
    if (isNil(columnIdsToShow)) {
      return this.columnsConfig();
    }
    return this.columnsConfig().filter(column => columnIdsToShow.includes(column.id));
  }

  #getDisplayedColumIds(): string[] {
    const columnIds = this.displayedColumns().map(column => column.id);

    if (this.rowActionsConfig() === undefined) {
      return columnIds;
    }
    return [...columnIds, 'settings'];
  }

  #verifyConfigIsValid(): void | never {
    this.#verifySettingsColumnValid();
    this.#verifySortConfigIsValid();
  }

  #verifySettingsColumnValid(): void | never {
    const settingsColumn = this.rowActionsConfig();
    if (isNil(settingsColumn)) {
      return;
    }
    if (isNil(settingsColumn.options) || settingsColumn.options.length === 0) {
      throw new TableInvalidConfigException(
        'Settings column config provided but no option presented in it.',
      );
    }
  }

  #verifySortConfigIsValid(): void | never {
    const sortableFields = this.columnsConfig()
      .filter(columConfig => columConfig.sortable)
      .map(columConfig => columConfig.field);
    if (areThereDuplicationInArray(sortableFields)) {
      throw new TableSortConfigException(
        'There is/are duplication in sortable fields which is not allowed.',
      );
    }
  }

  #verifyNoSortAndNoPagination(): void | never {
    const sortableFields = this.columnsConfig().filter(columConfig => columConfig.sortable);
    const noSortableField = sortableFields.length === 0;
    if (!noSortableField || this.showPaginator()) {
      throw new TableInvalidConfigException(
        'Sorting and/or pagination not allowed with Array and Observable based input in table.',
      );
    }
  }
}
